import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { ethers } from "ethers";
import { Web3Provider } from "@ethersproject/providers";
import { Contract } from "@ethersproject/contracts";
import BulkDisbursableNFTs from "../contracts/BulkDisbursableNFTs.abi";
import Roles from "../contracts/Roles.abi";
import { CONTRACT_ADDRESS, ROLES_LIBRARY_ADDRESS } from "../contracts/addresses";

interface Web3ContextProps {
  provider: Web3Provider | null;
  signer: ethers.Signer | null;
  address: string | null;
  chainId: number | null;
  connecting: boolean;
  connected: boolean;
  readContract: Contract | null;
  writeContract: Contract | null;
  rolesLibrary: Contract | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  error: string | null;
}

const Web3Context = createContext<Web3ContextProps>({
  provider: null,
  signer: null,
  address: null,
  chainId: null,
  connecting: false,
  connected: false,
  readContract: null,
  writeContract: null,
  rolesLibrary: null,
  connect: async () => {},
  disconnect: () => {},
  error: null,
});

export const useWeb3 = () => useContext(Web3Context);

interface Web3ProviderProps {
  children: ReactNode;
}

export const Web3ProviderWrapper: React.FC<Web3ProviderProps> = ({ children }) => {
  const [provider, setProvider] = useState<Web3Provider | null>(null);
  const [signer, setSigner] = useState<ethers.Signer | null>(null);
  const [address, setAddress] = useState<string | null>(null);
  const [chainId, setChainId] = useState<number | null>(null);
  const [connecting, setConnecting] = useState<boolean>(false);
  const [connected, setConnected] = useState<boolean>(false);
  const [readContract, setReadContract] = useState<Contract | null>(null);
  const [writeContract, setWriteContract] = useState<Contract | null>(null);
  const [rolesLibrary, setRolesLibrary] = useState<Contract | null>(null);
  const [error, setError] = useState<string | null>(null);

  const setupContracts = async (provider: Web3Provider, signer: ethers.Signer) => {
    try {
      // Set up read-only contract
      const roContract = new Contract(CONTRACT_ADDRESS, BulkDisbursableNFTs, provider);
      setReadContract(roContract);

      // Set up write contract
      const rwContract = new Contract(CONTRACT_ADDRESS, BulkDisbursableNFTs, signer);
      setWriteContract(rwContract);

      // Set up roles library
      const rolesContract = new Contract(ROLES_LIBRARY_ADDRESS, Roles, provider);
      setRolesLibrary(rolesContract);
    } catch (err) {
      console.error("Failed to setup contracts:", err);
      setError("Failed to setup contracts");
    }
  };

  const connect = async () => {
    if (!window.ethereum) {
      setError("MetaMask not installed");
      return;
    }

    setConnecting(true);
    setError(null);

    try {
      // Request account access
      await window.ethereum.request({ method: "eth_requestAccounts" });

      // Create Web3Provider
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      setProvider(provider);

      // Get signer
      const signer = provider.getSigner();
      setSigner(signer);

      // Get address
      const address = await signer.getAddress();
      setAddress(address);

      // Get network
      const network = await provider.getNetwork();
      setChainId(network.chainId);

      // Setup contracts
      await setupContracts(provider, signer);

      setConnected(true);
    } catch (err) {
      console.error("Failed to connect wallet:", err);
      setError("Failed to connect wallet");
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = () => {
    setProvider(null);
    setSigner(null);
    setAddress(null);
    setChainId(null);
    setConnected(false);
    setReadContract(null);
    setWriteContract(null);
    setRolesLibrary(null);
  };

  useEffect(() => {
    // Setup event listeners
    if (window.ethereum) {
      // Handle account changes
      window.ethereum.on("accountsChanged", (accounts: string[]) => {
        if (accounts.length === 0) {
          // User disconnected their wallet
          disconnect();
        } else {
          // Account changed, reconnect
          connect();
        }
      });

      // Handle chain changes
      window.ethereum.on("chainChanged", () => {
        // Chain changed, reload the page as recommended by MetaMask
        window.location.reload();
      });
    }

    // Check if already connected
    const checkConnection = async () => {
      if (window.ethereum) {
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        const accounts = await provider.listAccounts();
        
        if (accounts.length > 0) {
          connect();
        }
      }
    };

    checkConnection();

    return () => {
      // Remove event listeners if needed
      if (window.ethereum) {
        window.ethereum.removeAllListeners("accountsChanged");
        window.ethereum.removeAllListeners("chainChanged");
      }
    };
  }, []);

  return (
    <Web3Context.Provider
      value={{
        provider,
        signer,
        address,
        chainId,
        connecting,
        connected,
        readContract,
        writeContract,
        rolesLibrary,
        connect,
        disconnect,
        error,
      }}
    >
      {children}
    </Web3Context.Provider>
  );
};

// Add this declaration to make TypeScript happy with window.ethereum
declare global {
  interface Window {
    ethereum?: any;
  }
}